/**
 * T379 — Admin storage-migration API wrappers.
 *
 * Drives the `/admin/storage/status` + `/admin/storage/start` endpoints
 * (T377). T462: SFTP credentials no longer travel via the request body —
 * they are server-side env config. The frontend only sends `batchSize`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

export interface StorageMigrationStatus {
  byStatus: Record<string, number>;
}

export interface StorageMigrationSummary {
  considered: number;
  already_migrated: number;
  newly_migrated: number;
  failed: number;
  errors: Array<{ attachmentId: string; error: string }>;
}

export interface StorageMigrationStartInput {
  batchSize?: number;
}

export const adminStorageApi = {
  status: () => api.get<StorageMigrationStatus>('/admin/storage/status'),
  start: (input: StorageMigrationStartInput) =>
    api.post<StorageMigrationSummary>('/admin/storage/start', input),
};

export function useStorageMigrationStatus() {
  return useQuery({
    queryKey: ['admin-storage-status'],
    queryFn: () => adminStorageApi.status(),
    // Keep the status fresh during an active migration run — a 5 s poll
    // is cheap and lets the progress counters animate.
    refetchInterval: 5_000,
  });
}

export function useStartStorageMigration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StorageMigrationStartInput) => adminStorageApi.start(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-storage-status'] });
    },
  });
}
